import { useEffect, useRef } from 'react';
import { config } from '../../config';
import { SET_PIECE_SCHEMES, SET_PIECE_TYPES } from '../../domain/setPiece';
import {
  diagramActionB,
  MARKER_GLYPH_B,
  MARKER_LABEL_B,
  SCHEME_LABEL_B,
  typeSentenceB,
  TYPE_LABEL_B,
} from './setPieceB';
import { useSetPieceB } from './useSetPieceB';
import type { SetPieceBActions } from './useSetPieceB';
import type { MatchRow, SetPieceRow, SetPieceType } from '../../types';

/**
 * B-12 — **the set-piece sheet** (design spec §2.4; scenario 14; requirements §3.2.14):
 * type + scheme pickers, the penalty-area diagram, ≥44 pt markers placed by tap — every marker
 * tap commits the whole draft row (`commitSetPiece`, crash-safe SES-3), Done → `closeSetPiece`.
 *
 * ── A `<dialog>`, deliberately NON-modal ────────────────────────────────────
 *
 * §2.4 names it a `<dialog>` and lists it **inside the middle-band family** — and the engine
 * settles what that must mean: `CloseSetPiece` is deliberately not state-gated because *"a half
 * may end with the sheet open"*, which is only honorable if the whistle can still be pressed
 * while the sheet is up. `showModal()` inerts everything outside the dialog — the transition
 * control, the flag button, the undo pill — so this sheet uses `show()` and positions itself
 * over the **middle band only** (`.spb` anchors to `.capb-middle`). SheetB is not reused here
 * for exactly that reason: its `showModal()` is right for the tab shell's text-entry sheets and
 * wrong over live capture chrome (A's §5.6 reasoning, kept). The floor note stands regardless:
 * `<dialog>` is the 15.4-safe primitive, and the explicit Done is the one close path (a
 * non-modal dialog fires no `cancel`).
 *
 * ── Scenario 14: the board is undisturbed BEHIND it ─────────────────────────
 *
 * The capture surface stays mounted underneath — this sheet overlays it, it never replaces it.
 * The mechanism for "undisturbed" is the engine's: `SetPieceOp` cannot spell a position write, a
 * count change or a transition, so nothing done in here can compile a board disturbance. The
 * browser pass asserts the observable half: every tile box byte-equal while the sheet is open
 * and after it closes.
 *
 * ── LIVE-9's shape ──────────────────────────────────────────────────────────
 *
 * Everything on this sheet is a tap: the kind, the markers, the scheme, clearing, Done. B ships
 * no free-text here at all (§2.4 lists none — A's optional note is A's).
 */

export interface SetPieceSheetBProps {
  /** The SHELL's authoritative row — re-read after every committed transition (B-10's rule). */
  readonly match: MatchRow;
  /** Done committed (or nothing was open) — the owner unmounts the sheet. */
  readonly onClose: () => void;
}

export function SetPieceSheetB({ match, onClose }: SetPieceSheetBProps) {
  const { state, actions } = useSetPieceB(match);
  const dialog = useRef<HTMLDialogElement | null>(null);

  /** Non-modal on purpose (see the header): the page around the sheet stays live. */
  useEffect(() => {
    const element = dialog.current;
    if (element === null || element.open) return;
    element.show();
  }, []);

  return (
    <dialog
      ref={dialog}
      className="spb"
      aria-labelledby="spb-title"
      data-testid="spb"
      data-capturing={state.capturing ? 'true' : 'false'}
    >
      <header className="spb-bar">
        <h2 className="spb-title" id="spb-title">
          Set piece
        </h2>
        {/* §2.4 — "Done → closeSetPiece". The one way out, top-placed (the SheetB discipline:
            the primary action where nothing can cover it), and it leaves only after the close
            COMMITS — a failed close keeps the sheet open with the reason. */}
        <button
          type="button"
          className="spb-done"
          data-testid="spb-done"
          onClick={() => {
            void actions.finish().then((closed) => {
              if (closed) onClose();
            });
          }}
        >
          Done
        </button>
      </header>

      {/* §6.4a's negative, said out loud — the same sentence the other capture surfaces render:
          pre-kickoff the sheet opens, and starting a capture is declined with the reason. */}
      {!state.capturing && (
        <p className="spb-preview" data-testid="spb-preview">
          Not kicked off — set pieces record nothing yet.
        </p>
      )}

      {state.notice !== null && (
        <div className="spb-notice" role="status" data-testid="spb-notice">
          <span>{state.notice}</span>
          <button type="button" onClick={actions.dismissNotice}>
            Dismiss
          </button>
        </div>
      )}

      {state.phase === 'loading' ? (
        <p className="spb-idle">Opening…</p>
      ) : (
        <>
          {/* §2.4's type picker — generated from SET_PIECE_TYPES, so a kind is a data change and
              not a button someone must remember. B-23 collected on that: free kicks arrived as two
              rows in the engine's table and this row rendered them without being edited. Starting a
              kind opens a fresh capture (the minute stamps NOW — SES-10); any open capture is
              closed first.

              Four kinds, so a 2×2 grid rather than a four-across strip: four buttons across a
              390 pt sheet leaves ~83 pt each, below the target size a thumb needs and far below
              what the words need. Columns are the side, rows are the restart — a stable map, and
              the label says both anyway so nothing depends on remembering the layout. */}
          <div className="spb-kinds" role="group" aria-label="Capture a set piece">
            {SET_PIECE_TYPES.map((type: SetPieceType) => (
              <button
                key={type}
                type="button"
                className="spb-kind"
                data-testid={`spb-start-${type}`}
                data-active={state.draft?.type === type ? 'true' : undefined}
                disabled={!state.capturing}
                onClick={() => {
                  actions.start(type);
                }}
              >
                <span className="spb-kind__restart">{TYPE_LABEL_B[type].restart}</span>
                <span className="spb-kind__side">{TYPE_LABEL_B[type].side}</span>
              </button>
            ))}
          </div>

          {state.draft === null ? (
            <p className="spb-idle" data-testid="spb-idle">
              Pick a kind above to start a capture. Everything on this sheet is a tap.
            </p>
          ) : (
            <SetPieceCaptureB draft={state.draft} actions={actions} />
          )}
        </>
      )}
    </dialog>
  );
}

/** The open capture: minute line, the diagram with its markers, the scheme picker. */
function SetPieceCaptureB({
  draft,
  actions,
}: {
  readonly draft: SetPieceRow;
  readonly actions: SetPieceBActions;
}) {
  const diagram = useRef<HTMLDivElement | null>(null);

  /**
   * One contact, one meaning, resolved from the element the finger landed on (`diagramActionB`):
   * a tap on a placed marker cycles that marker's type; a tap anywhere else drops a new marker
   * at the engine-normalized point. The box is read **at the moment of the tap**, from the
   * element that was touched, so the normalization is against the geometry the user actually
   * saw. Commit is per tap; the marker renders only when its write commits (SES-2 — the marker
   * appearing IS the acknowledgment).
   */
  const onDiagramPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const marker = (event.target as HTMLElement).closest<HTMLElement>('[data-marker-index]');
    const element = diagram.current;
    if (element === null) return;
    const action = diagramActionB(
      marker?.dataset.markerIndex,
      element.getBoundingClientRect(),
      event.clientX,
      event.clientY,
    );
    if (action.kind === 'cycle') actions.cycle(action.index);
    else if (action.kind === 'drop') actions.drop(action.point);
  };

  return (
    <div className="spb-capture" data-testid="spb-capture" data-type={draft.type}>
      {/* SES-10 — the minute was stamped ONCE, at draft creation; this renders the stored fact.
          The kind is spelled as a sentence here (not the picker's two stacked words) because this
          line is read, not scanned. */}
      <p className="spb-minute" data-testid="spb-minute">
        {typeSentenceB(draft.type)} · <strong>{draft.matchMinute}′</strong>
      </p>

      {/* The penalty-area diagram. Aspect ratio arrives INLINE from the same config constant
          the geometry contract names (`SET_PIECE_DIAGRAM_ASPECT`) — a stylesheet copy would be
          a second number waiting to drift, and a box that reflowed to a different ratio would
          move every stored marker relative to the six-yard box under it. Markers are positioned
          as percentages of this box, so a re-render and a different viewport put (0.5, 0.5) at
          the same place on the pitch. */}
      <div
        ref={diagram}
        className="spb-diagram"
        data-testid="spb-diagram"
        style={{ aspectRatio: String(config.SET_PIECE_DIAGRAM_ASPECT) }}
        onPointerDown={onDiagramPointerDown}
      >
        <div className="spb-diagram__box" aria-hidden="true" />
        <div className="spb-diagram__six" aria-hidden="true" />
        <div className="spb-diagram__goal" aria-hidden="true" />
        <div className="spb-diagram__arc" aria-hidden="true" />

        {draft.markers.map((marker, index) => (
          <button
            // Index IS the marker's identity, and it is stable: `placeMarker` appends and
            // `cycleMarkerAt` replaces in place, so no marker changes index under a tap.
            key={index}
            type="button"
            className="spb-marker"
            data-marker-index={index}
            data-marker-type={marker.type}
            data-testid={`spb-marker-${String(index)}`}
            aria-label={`${MARKER_LABEL_B[marker.type]} — tap to change`}
            /* ≥44 pt per §2.4, sized INLINE from the config constant that names the obligation
               (`SET_PIECE_MARKER_PT`) — B renders the number the config states (the A-side
               44-vs-60 divergence is A's recorded state, not a convention). */
            style={{
              left: `${String(marker.x * 100)}%`,
              top: `${String(marker.y * 100)}%`,
              width: `${String(config.SET_PIECE_MARKER_PT)}px`,
              height: `${String(config.SET_PIECE_MARKER_PT)}px`,
            }}
          >
            <span className="spb-marker__glyph" aria-hidden="true">
              {MARKER_GLYPH_B[marker.type]}
            </span>
          </button>
        ))}
      </div>

      <div className="spb-legend" data-testid="spb-legend">
        {draft.markers.length === 0 ? (
          <span>Tap the diagram to drop a marker. Tap a marker again to change what it means.</span>
        ) : (
          <>
            <span>
              {draft.markers.length} marker{draft.markers.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              className="spb-clear"
              data-testid="spb-clear"
              onClick={actions.clearMarkers}
            >
              Clear markers
            </button>
          </>
        )}
      </div>

      {/* §2.4's scheme picker — one segmented control for both kinds (B's simplification; the
          column is one nullable field). Selected is never hue-only: heavier border + weight. */}
      <div className="spb-group" role="group" aria-label="Marking scheme">
        <span className="spb-group__label">Scheme</span>
        <div className="spb-seg">
          {SET_PIECE_SCHEMES.map((scheme) => (
            <button
              key={scheme}
              type="button"
              className="spb-seg__option"
              aria-pressed={draft.scheme === scheme}
              data-testid={`spb-scheme-${scheme}`}
              onClick={() => {
                actions.setScheme(scheme);
              }}
            >
              {SCHEME_LABEL_B[scheme]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
